import { format, subDays } from 'date-fns';

import { LoyaltySmgCodeStatus } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';
import { US_DATE_FORMAT, US_LONG_DATE_FORMAT } from 'utils/dateTime';

import { ITransactionDateOption } from './transaction-form/transaction-date-input-form';
import { ClaimMethod, ClaimPointsUIData } from './types';

export const mapStatusCodeToSanityContent = (statusCode?: string, data?: ClaimPointsUIData) => {
  let sanityContent: any = null;

  switch (statusCode) {
    case LoyaltySmgCodeStatus.THROTTLING_ERROR:
      sanityContent = data?.claimPointsTooManyRequests;
      break;
    case LoyaltySmgCodeStatus.CODE_EXISTS:
      sanityContent = data?.claimPointsDuplicateTransactionId;
      break;
    case LoyaltySmgCodeStatus.EXPIRED:
      sanityContent = data?.claimPointsExpiredContent;
      break;
    case LoyaltySmgCodeStatus.RETRY_REQUESTED:
      sanityContent = data?.claimPointsPendingContent;
      break;
    case LoyaltySmgCodeStatus.TRANSACTION_CLAIMED:
      sanityContent = data?.claimPointsAlreadyClaimedContent;
      break;
    case LoyaltySmgCodeStatus.NOT_FOUND:
      sanityContent = data?.claimPointsTransactionNotFoundContent;
      break;
    case LoyaltySmgCodeStatus.INVALID_RESTAURANT:
      sanityContent = data?.claimPointsInvalidRestaurantContent;
      break;
    case LoyaltySmgCodeStatus.LIMIT_EXCEEDED:
      sanityContent = data?.claimPointsDailyAmountExceededContent;
      break;
    case LoyaltySmgCodeStatus.INVALID_FORMAT:
    default: {
      sanityContent = data?.claimPointsInvalidCodeContent;
      break;
    }
  }

  return sanityContent;
};

export const isClaimPointsHref = (path?: string | null) => {
  if (!path) {
    return false;
  }

  return path === routes.claimPointsBase || path.startsWith(`${routes.claimPointsBase}/`);
};

type ClaimStackRoute = {
  name?: string;
  path?: string;
};

/** True when the screen under the in-flight claim is a previous accepted result. */
export const previousRouteIsClaimResult = (stackRoutes?: readonly ClaimStackRoute[]) => {
  const previous = stackRoutes?.[stackRoutes.length - 2];
  if (!previous) {
    return false;
  }

  return previous.name === 'accepted' || Boolean(previous.path?.endsWith('/accepted'));
};

export const getBackUrl = (baseUrl: string, claimMethod?: ClaimMethod) => {
  let url = baseUrl;

  switch (claimMethod) {
    case ClaimMethod.MANUAL:
      url = `${baseUrl}${routes.claimPointsSurveyRoot}`;
      break;
    case ClaimMethod.QR_SCAN:
      url = `${baseUrl}${routes.scan}`;
      break;
    default:
      url = baseUrl;
      break;
  }

  return url;
};

export const generateTransactionDateOptions = ({
  validReceiptHours,
  currentDate,
}: {
  validReceiptHours: number;
  currentDate: number;
}): ITransactionDateOption[] => {
  const HOURS_IN_DAY = 24;
  const dropdownOptions: ITransactionDateOption[] = [];
  let validDays = Math.floor(validReceiptHours / HOURS_IN_DAY);

  if (!validDays) {
    return dropdownOptions;
  }
  // Adding one day because if the 48 hours falls outside the last two days based on timing, the user is unable to claim their points.
  validDays += 1;

  for (let day = 0; day < validDays; day++) {
    const optionDay = subDays(currentDate, day);

    dropdownOptions.push({
      value: format(optionDay, US_DATE_FORMAT),
      label: format(optionDay, US_LONG_DATE_FORMAT),
    });
  }

  return dropdownOptions;
};
